import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

const TOKEN_KEY = "dsite_token";

function readStoredRole() {
  // Prefer explicit role key; fall back to inferring from previous storage shape
  const explicit = localStorage.getItem("dsite_role");
  if (explicit) return explicit;
  const adminRole = localStorage.getItem("dsite_admin_role");
  if (adminRole) return adminRole;
  const userRaw = localStorage.getItem("dsite_user");
  if (userRaw) {
    try {
      const u = JSON.parse(userRaw);
      if (u?.role) return u.role;
    } catch {
      /* ignore */
    }
  }
  return null;
}

function clearAuthStorage() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem("dsite_role");
  localStorage.removeItem("dsite_user");
  localStorage.removeItem("dsite_admin_email");
  localStorage.removeItem("dsite_admin_role");
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null); // 'super_admin' | 'sub_admin' | 'customer'
  const [loading, setLoading] = useState(true);

  const isAdmin = role === "super_admin" || role === "sub_admin";
  const isCustomer = role === "customer";

  const bootstrap = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setUser(null);
      setRole(null);
      setLoading(false);
      return;
    }
    const storedRole = readStoredRole();

    // Try the endpoint matching stored role first, then fall back to the other one
    const tryAdmin = async () => {
      const res = await api.get("/auth/me");
      const me = res.data || {};
      const r = me.role || "super_admin";
      localStorage.setItem("dsite_role", r);
      localStorage.setItem("dsite_admin_email", me.email || "");
      localStorage.setItem("dsite_admin_role", r);
      setUser({
        email: me.email,
        name: me.name,
        display_name: me.display_name || me.name,
        role: r,
        id: me.id,
        department: me.department || null,
        sales_level: me.sales_level || null,
        team_id: me.team_id || null,
        login_id: me.login_id || null,
      });
      setRole(r);
    };
    const tryCustomer = async () => {
      const res = await api.get("/customer/me");
      const me = res.data || {};
      localStorage.setItem("dsite_role", "customer");
      localStorage.setItem(
        "dsite_user",
        JSON.stringify({
          id: me.id,
          email: me.email,
          name: me.name,
          phone: me.phone || "",
          company: me.company || "",
          role: "customer",
        })
      );
      setUser({ ...me, role: "customer" });
      setRole("customer");
    };

    try {
      if (storedRole === "customer") {
        try {
          await tryCustomer();
        } catch (e) {
          if (e?.response?.status === 401 || e?.response?.status === 403) {
            await tryAdmin();
          } else {
            throw e;
          }
        }
      } else {
        try {
          await tryAdmin();
        } catch (e) {
          if (e?.response?.status === 401 || e?.response?.status === 403) {
            await tryCustomer();
          } else {
            throw e;
          }
        }
      }
    } catch (err) {
      // Token invalid for both — clear storage
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        clearAuthStorage();
        setUser(null);
        setRole(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  // Cross-tab sync
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === TOKEN_KEY) {
        bootstrap();
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [bootstrap]);

  const login = useCallback(async ({ kind, email, password }) => {
    // kind: 'admin' | 'customer'
    const path = kind === "admin" ? "/auth/login" : "/customer/login";
    const res = await api.post(path, { email, password });
    const data = res.data || {};
    localStorage.setItem(TOKEN_KEY, data.access_token);

    if (kind === "admin") {
      const r = data.role || "super_admin";
      localStorage.setItem("dsite_role", r);
      localStorage.setItem("dsite_admin_email", data.email || email);
      localStorage.setItem("dsite_admin_role", r);
      const u = {
        email: data.email || email,
        name: data.name || "",
        display_name: data.display_name || data.name || "",
        role: r,
        id: data.id,
        department: data.department || null,
        sales_level: data.sales_level || null,
        team_id: data.team_id || null,
        login_id: data.login_id || null,
      };
      setUser(u);
      setRole(r);
      return u;
    } else {
      localStorage.setItem("dsite_role", "customer");
      try {
        const meRes = await api.get("/customer/me");
        const me = meRes.data || {};
        const u = {
          id: me.id,
          email: me.email || data.user?.email || email,
          name: me.name || data.user?.name || "",
          phone: me.phone || "",
          company: me.company || "",
          role: "customer",
        };
        localStorage.setItem("dsite_user", JSON.stringify(u));
        setUser(u);
        setRole("customer");
        return u;
      } catch {
        const u = { ...(data.user || {}), role: "customer" };
        localStorage.setItem("dsite_user", JSON.stringify(u));
        setUser(u);
        setRole("customer");
        return u;
      }
    }
  }, []);

  const logout = useCallback(() => {
    clearAuthStorage();
    setUser(null);
    setRole(null);
  }, []);

  const value = {
    user,
    role,
    isAdmin,
    isCustomer,
    isAuthenticated: !!user,
    loading,
    login,
    logout,
    refresh: bootstrap,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
